import React, { lazy } from 'react'
import { Outlet } from 'react-router-dom'

import { PostBreadcrumbDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { MetaContext } from 'modules/core/lib/navigation/routes'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const RootPage = React.lazy(() => import('modules/posts/pages/PostsRootPage'))
const CommunitiesFeatureFlagGuard = React.lazy(
  () => import('modules/posts/pages/CommunitiesFeatureFlagGuard')
)
const ShowCommunityPage = lazy(
  () => import('modules/posts/pages/ShowCommunityPage/ShowCommunityPage')
)
const NewPostPage = lazy(() => import('modules/posts/pages/NewPostPage'))

const ShowPostPage = lazy(() => import('modules/posts/pages/ShowPostPage'))
const EditPostPage = lazy(() => import('modules/posts/pages/EditPostPage'))
const PostInsightsPage = lazy(() => import('modules/posts/pages/PostInsightsPage/PostInsightsPage'))
const PostInsightsFeatureFlagGuard = lazy(
  () => import('modules/posts/pages/PostInsightsPage/PostInsightsFeatureFlagGuard')
)
const EditGroupPage = lazy(() => import('modules/posts/pages/EditGroupPage'))
const NewGroupPage = lazy(() => import('modules/posts/pages/NewGroupPage'))
const LeaveGroupPage = lazy(() => import('modules/posts/pages/LeaveGroupPage'))
const DeleteGroupPage = lazy(() => import('modules/posts/pages/DeleteGroupPage'))
const ManageMembersGroupPage = lazy(() => import('modules/posts/pages/ManageMembersGroupPage'))
const PostsListPage = lazy(() => import('modules/posts/pages/PostsListPage/PostsListPage'))

export const postsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.communities.path,
      element: (
        <CommunitiesFeatureFlagGuard>
          <RootPage />
        </CommunitiesFeatureFlagGuard>
      ),
      handle: {
        module: 'communities',
        moduleName: () => i18n.t('posts.communities.communities_title'),
      },
      children: [
        {
          index: true,
          element: <ShowCommunityPage />,
        },
        {
          path: navigation.communities.new.path,
          element: <ShowCommunityPage />,
          children: [
            {
              index: true,
              element: <NewGroupPage />,
            },
          ],
        },
        {
          path: navigation.communities.id.path,
          element: <ShowCommunityPage />,
          children: [
            {
              path: navigation.communities.id.new.path,
              element: <NewGroupPage />,
            },
            {
              path: navigation.communities.id.edit.path,
              element: <EditGroupPage />,
            },
            {
              path: navigation.communities.id.leave.path,
              element: <LeaveGroupPage />,
            },
            {
              path: navigation.communities.id.delete.path,
              element: <DeleteGroupPage />,
            },
            {
              path: navigation.communities.id.addMembers.path,
              element: <ManageMembersGroupPage action="add" />,
            },
            {
              path: navigation.communities.id.removeMembers.path,
              element: <ManageMembersGroupPage action="remove" />,
            },
          ],
        },
        {
          path: navigation.communities.posts.path,
          children: [
            {
              index: true,
              element: <PostsListPage />,
            },
          ],
        },
        {
          path: navigation.communities.post.path,
          children: [
            {
              path: navigation.communities.post.new.path,
              element: <NewPostPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('posts.communities.new_post'),
                }),
              },
            },
            {
              path: navigation.communities.post.newEvent.path,
              element: <NewPostPage isEvent />,
              handle: {
                meta: () => ({
                  title: i18n.t('posts.communities.new_event_post'),
                }),
              },
            },
            {
              path: navigation.communities.post.id.path,
              element: <Outlet />,
              handle: {
                meta: retrieveTitle,
              },
              children: [
                {
                  index: true,
                  element: <ShowPostPage />,
                },
                {
                  path: navigation.communities.post.id.edit.path,
                  element: <EditPostPage />,
                },
                {
                  path: navigation.communities.post.id.insights.path,
                  element: (
                    <PostInsightsFeatureFlagGuard>
                      <PostInsightsPage />
                    </PostInsightsFeatureFlagGuard>
                  ),
                  handle: {
                    meta: () => ({
                      title: i18n.t('posts.communities.insights.breadcrumb'),
                    }),
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}

const retrieveTitle = ({ params, watchQuery }: MetaContext) => {
  return watchQuery({
    query: PostBreadcrumbDocument,
    variables: {
      id: params['id']!,
    },
  }).map(({ data }) => {
    const post = data.posts.post
    const title = (post ? post.title : '') ?? ''

    return { title }
  })
}
